import {useEffect, useRef} from 'react';
import {CommandSearchRegistry} from './registry';
import {scopesEqual, toScopePath} from '../scope/keys';
import type {CommandSearchScope} from '../scope/types';

export interface UseCommandSearchScopeOptions {
    /** Provider ids to mount when this scope becomes active. */
    mountProviderIds?: string[];
    /** Whether to invalidate scoped cache on unmount. Defaults to true. */
    invalidateOnUnmount?: boolean;
}

/**
 * Syncs the active command-search scope and manages scoped provider mounts.
 * Call from route layouts when entering customer/vendor/account scope.
 */
export const useCommandSearchScope = (
    scope: CommandSearchScope,
    options?: UseCommandSearchScopeOptions,
): void => {
    const scopePathRef = useRef(toScopePath(scope));
    scopePathRef.current = toScopePath(scope);

    useEffect(() => {
        CommandSearchRegistry.setScope(scope);

        options?.mountProviderIds?.forEach((providerId) => {
            CommandSearchRegistry.mount(providerId, {scope});
        });

        return () => {
            if (options?.mountProviderIds?.length) {
                options.mountProviderIds.forEach((providerId) => {
                    CommandSearchRegistry.unmount(providerId, {scope});
                });
            }

            if (options?.invalidateOnUnmount !== false) {
                CommandSearchRegistry.invalidate({scopePath: scopePathRef.current});
            }

            const globalScope = {panelId: scope.panelId, stack: [] as CommandSearchScope['stack']};
            if (!scopesEqual(scope, globalScope)) {
                CommandSearchRegistry.setScope(globalScope);
            }
        };
    }, [toScopePath(scope), options?.invalidateOnUnmount, options?.mountProviderIds?.join(',')]);
};
